/**
 * Inkbond Design Studio — Canvas Editor Controller
 * Owala-inspired panel UI with Fabric.js v6
 * Text auto-adds to canvas on panel open, updates live as user types/changes settings
 */

(function () {
  'use strict';

  // Measure actual header height and set CSS variable for studio layout
  function setStudioOffset() {
    var studio = document.querySelector('.ib-studio');
    if (!studio) return;
    var rect = studio.getBoundingClientRect();
    var offset = Math.max(rect.top, 0);
    studio.style.height = 'calc(100vh - ' + offset + 'px)';
    studio.style.maxHeight = 'calc(100dvh - ' + offset + 'px)';
  }
  // Run on load, after layout settles, and on resize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setStudioOffset();
      setTimeout(setStudioOffset, 300);
    });
  } else {
    setStudioOffset();
    setTimeout(setStudioOffset, 300);
  }
  window.addEventListener('load', function () { setTimeout(setStudioOffset, 100); });
  window.addEventListener('resize', setStudioOffset);

  function waitForFabric(cb) {
    if (typeof fabric !== 'undefined') return cb();
    var checks = 0;
    var interval = setInterval(function () {
      checks++;
      if (typeof fabric !== 'undefined') { clearInterval(interval); cb(); }
      if (checks > 100) { clearInterval(interval); console.error('Fabric.js failed to load'); }
    }, 100);
  }

  // ── Constants ── (2" x 3.5" single tattoo at 300 DPI)
  var SHEET_RATIO = 3.5 / 2; // height / width
  var PROD_WIDTH = 600;       // 300 DPI * 2"
  var PROD_HEIGHT = 1050;     // 300 DPI * 3.5"
  // Product variants for "Custom Design Tattoo - Studio"
  var VARIANTS = [
    { id: 54882250883363, label: '10 Pack', price: '$24.99' },
    { id: 54882250916131, label: '20 Pack (Save $10)', price: '$34.99' },
    { id: 54882250948899, label: '40 Pack (Save $30) - Ships Free', price: '$49.99' },
    { id: 54882250981667, label: '60 Pack (Save $55) - Ships Free', price: '$64.99' },
    { id: 54882251014435, label: '80 Pack (Save $85) - Ships Free', price: '$84.99' },
    { id: 54882251047203, label: '100 Pack (Save $100) - Ships Free', price: '$99.99' },
    { id: 54882251079971, label: '150 Pack (Save $170) - Ships Free', price: '$129.99' },
    { id: 54882251112739, label: '200 Pack (Save $245) - Ships Free', price: '$169.99' }
  ];
  var VARIANT_ID = VARIANTS[0].id;
  var WORKER_URL = 'https://inkbond-studio.inkbond-studio.workers.dev';

  // ── Studio Class ──
  function InkbondStudio() {
    this.canvas = null;
    this.history = [];
    this.historyIndex = -1;
    this.maxHistory = 50;
    this.isLoadingState = false;
    this.previewDataUrl = null;
    this.selectedColor = '#000000';
    this.selectedFont = 'Arial';
    this.selectedSize = 24;
    this.selectedStroke = null;
    this.selectedStrokeWidth = 0;

    // The "live" text object currently being edited via the text panel
    this.activeTextObj = null;

    this.init();
  }

  InkbondStudio.prototype.init = function () {
    var self = this;
    var size = this.getCanvasSize();

    this.canvas = new fabric.Canvas('ib-studio-canvas', {
      width: size.width,
      height: size.height,
      backgroundColor: null,
      selection: true,
      preserveObjectStacking: true
    });

    this.drawGuides();
    this.saveState();
    this.bindEvents();
    // Center snapping removed per user request
    this._setupPinchZoom();
    this._syncToolbarHeight();
    this._restoreAutoSave();
    this._setupAutoSave();
    this._setupLeaveWarning();
    this._setupDoubleTapEdit();

    window.addEventListener('resize', function () { self.handleResize(); self._syncToolbarHeight(); });

    // Keyboard shortcuts
    document.addEventListener('keydown', function (e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (self.canvas.getActiveObject() && self.canvas.getActiveObject().isEditing) return;
        e.preventDefault();
        self.deleteSelected();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) { self.redo(); } else { self.undo(); }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        self.duplicateSelected();
      }
    });

    console.log('Inkbond Studio initialized', size.width + 'x' + size.height);
  };

  InkbondStudio.prototype.getCanvasSize = function () {
    var area = document.querySelector('.ib-studio__canvas-area');
    if (!area) return { width: 600, height: 776 };

    var isMobile = window.innerWidth <= 768;
    var areaRect = area.getBoundingClientRect();

    var maxWidth, maxHeight;

    if (isMobile) {
      // On mobile: subtract toolbar width (48px) + padding (12px each side)
      var toolbarEl = area.querySelector('.ib-studio__canvas-toolbar');
      var toolbarW = toolbarEl ? toolbarEl.getBoundingClientRect().width + 12 : 56;
      maxWidth = areaRect.width - toolbarW - 16;

      // Calculate available height: viewport minus everything above and below
      var studioEl = document.querySelector('.ib-studio');
      var headerEl = document.querySelector('header.header, .header');
      var headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      var studioHeader = document.querySelector('.ib-studio__header');
      var studioHeaderH = studioHeader ? studioHeader.getBoundingClientRect().height : 0;
      // Bottom: tool tabs (~60px) + cart bar (~60px) + padding
      var bottomH = 140;
      maxHeight = window.innerHeight - headerH - studioHeaderH - bottomH - 32;
      // Cap canvas height so bottom isn't cut off
      maxHeight = Math.min(maxHeight, window.innerHeight * 0.55);
    } else {
      maxWidth = areaRect.width - 24;
      maxHeight = areaRect.height - 24;
    }

    var width = Math.min(maxWidth, maxHeight / SHEET_RATIO);
    width = Math.min(width, isMobile ? 360 : 440);
    width = Math.max(width, 180);
    var height = Math.round(width * SHEET_RATIO);

    if (height > maxHeight) {
      height = maxHeight;
      width = Math.round(height / SHEET_RATIO);
    }

    return { width: Math.round(width), height: Math.round(height) };
  };

  InkbondStudio.prototype.handleResize = function () {
    var size = this.getCanvasSize();
    var scaleX = size.width / this.canvas.getWidth();
    var scaleY = size.height / this.canvas.getHeight();

    this.canvas.setDimensions({ width: size.width, height: size.height });

    this.canvas.getObjects().forEach(function (obj) {
      if (obj._isGuide) return;
      obj.scaleX = obj.scaleX * scaleX;
      obj.scaleY = obj.scaleY * scaleY;
      obj.left = obj.left * scaleX;
      obj.top = obj.top * scaleY;
      obj.setCoords();
    });

    this.drawGuides();
    this.canvas.renderAll();
  };

  InkbondStudio.prototype.drawGuides = function () {
    // Remove any existing guides
    var self = this;
    this.canvas.getObjects().forEach(function (obj) {
      if (obj._isGuide && !obj._isSnapLine) self.canvas.remove(obj);
    });
    // No dashed border — full canvas is the print area (bleed handled in production)
  };

  // ── Center Snapping (like Canva) with hysteresis to prevent wiggle ──
  InkbondStudio.prototype._setupSnapping = function () {
    var self = this;
    var SNAP_IN = 8;    // pixels to trigger snap
    var SNAP_OUT = 20;  // pixels to release snap (must be larger than SNAP_IN)
    var isSnappedV = false;
    var isSnappedH = false;
    var snapOffsetV = 0; // difference between mouse position and snapped position
    var snapOffsetH = 0;
    var snapLineH = null;
    var snapLineV = null;

    function getSnapLines() {
      if (!snapLineH) {
        snapLineH = new fabric.Line([0, 0, 0, 0], {
          stroke: '#ff4081', strokeWidth: 1, strokeDashArray: [3, 3],
          selectable: false, evented: false, excludeFromExport: true
        });
        snapLineH._isSnapLine = true;
        snapLineH._isGuide = true;
      }
      if (!snapLineV) {
        snapLineV = new fabric.Line([0, 0, 0, 0], {
          stroke: '#ff4081', strokeWidth: 1, strokeDashArray: [3, 3],
          selectable: false, evented: false, excludeFromExport: true
        });
        snapLineV._isSnapLine = true;
        snapLineV._isGuide = true;
      }
      return { h: snapLineH, v: snapLineV };
    }

    // Reset snap state when object is released
    self.canvas.on('mouse:up', function () {
      isSnappedV = false;
      isSnappedH = false;
      snapOffsetV = 0;
      snapOffsetH = 0;
    });

    self.canvas.on('object:moving', function (e) {
      var obj = e.target;
      if (!obj || obj._isGuide) return;

      var canvasW = self.canvas.getWidth();
      var canvasH = self.canvas.getHeight();
      var centerX = canvasW / 2;
      var centerY = canvasH / 2;

      var bound = obj.getBoundingRect(true);
      var objCenterX = bound.left + bound.width / 2;
      var objCenterY = bound.top + bound.height / 2;

      var lines = getSnapLines();
      var distV = Math.abs(objCenterX - centerX);
      var distH = Math.abs(objCenterY - centerY);

      var snappedV = false;
      var snappedH = false;

      // Vertical center snap (left/right axis)
      if (isSnappedV) {
        // Already snapped — only release if dragged far enough away
        if (distV > SNAP_OUT) {
          isSnappedV = false;
          snapOffsetV = 0;
        } else {
          // Stay snapped — keep object at center
          obj.left = obj.left + (centerX - objCenterX);
          snappedV = true;
        }
      } else {
        // Not snapped — check if close enough to snap in
        if (distV < SNAP_IN) {
          obj.left = obj.left + (centerX - objCenterX);
          isSnappedV = true;
          snappedV = true;
        }
      }

      // Horizontal center snap (up/down axis)
      if (isSnappedH) {
        if (distH > SNAP_OUT) {
          isSnappedH = false;
          snapOffsetH = 0;
        } else {
          obj.top = obj.top + (centerY - objCenterY);
          snappedH = true;
        }
      } else {
        if (distH < SNAP_IN) {
          obj.top = obj.top + (centerY - objCenterY);
          isSnappedH = true;
          snappedH = true;
        }
      }

      // Show/hide snap lines
      if (snappedV) {
        lines.v.set({ x1: centerX, y1: 0, x2: centerX, y2: canvasH, visible: true });
        if (!self.canvas.getObjects().includes(lines.v)) self.canvas.add(lines.v);
      } else {
        lines.v.set('visible', false);
      }
      if (snappedH) {
        lines.h.set({ x1: 0, y1: centerY, x2: canvasW, y2: centerY, visible: true });
        if (!self.canvas.getObjects().includes(lines.h)) self.canvas.add(lines.h);
      } else {
        lines.h.set('visible', false);
      }
      if (snappedV || snappedH) obj.setCoords();

      self.canvas.renderAll();
    });

    self.canvas.on('object:modified', function () {
      // Hide snap lines after drop
      if (snapLineH) snapLineH.set('visible', false);
      if (snapLineV) snapLineV.set('visible', false);
      self.canvas.renderAll();
    });
  };

  // ── State / Undo / Redo ──
  InkbondStudio.prototype.saveState = function () {
    if (this.isLoadingState) return;
    // Save full canvas JSON including _isGuide flag
    var json = this.canvas.toJSON(['_isGuide', '_isSnapLine', 'lockUniScaling']);
    this.history.splice(this.historyIndex + 1);
    this.history.push(JSON.stringify(json));
    if (this.history.length > this.maxHistory) this.history.shift();
    this.historyIndex = this.history.length - 1;
  };

  InkbondStudio.prototype.loadState = function (json) {
    var self = this;
    this.isLoadingState = true;

    function afterLoad() {
      if (!self.isLoadingState) return; // prevent double-call
      self.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) {
          obj.set({ selectable: false, evented: false, excludeFromExport: true });
        }
        if (obj.type === 'image' && obj.lockUniScaling) {
          self._lockImageControls(obj);
        }
      });
      self.canvas.renderAll();
      self.isLoadingState = false;
      self.activeTextObj = null;
      self.updateHasObjects();
      self.refreshLayers();
    }

    var result = this.canvas.loadFromJSON(json);
    // Fabric.js v6 returns a Promise
    if (result && typeof result.then === 'function') {
      result.then(function () { afterLoad(); }).catch(function (err) {
        console.error('loadFromJSON error:', err);
        self.isLoadingState = false;
      });
    } else {
      // Fallback for v5-style (synchronous or callback)
      afterLoad();
    }
  };

  InkbondStudio.prototype.undo = function () {
    if (this.historyIndex <= 0) return;
    this.historyIndex--;
    this.loadState(this.history[this.historyIndex]);
  };

  InkbondStudio.prototype.redo = function () {
    if (this.historyIndex >= this.history.length - 1) return;
    this.historyIndex++;
    this.loadState(this.history[this.historyIndex]);
  };

  // ── Text Panel: preview mode (no auto-add) ──
  InkbondStudio.prototype.openTextPanel = function () {
    // Fully reset for new text creation
    this.activeTextObj = null;
    this._setAlpineData('textEditing', false);

    // Reset text inputs
    var inputs = document.querySelectorAll('.js-studio-text-input');
    inputs.forEach(function (inp) { inp.value = ''; });

    // Reset preview
    this.updatePreview('Your text');

    // Reset selected styles to defaults
    this.selectedColor = '#000000';
    this.selectedFont = 'Arial';
    this.selectedSize = 24;
    this.selectedStroke = null;
    this.selectedStrokeWidth = 0;
  };

  // Add text to canvas from the preview panel
  InkbondStudio.prototype.addTextToCanvas = function () {
    var inputs = document.querySelectorAll('.js-studio-text-input');
    var val = (inputs[0] ? inputs[0].value : '') || 'Your text';

    // Offset each new text so they don't stack
    var textCount = this.canvas.getObjects().filter(function (o) { return (o.type === 'i-text' || o.type === 'text') && !o._isGuide; }).length;
    var offset = textCount * 20;
    var textOpts = {
      left: this.canvas.getWidth() / 2 + offset,
      top: this.canvas.getHeight() / 3 + offset,
      originX: 'center',
      originY: 'center',
      fontSize: this.selectedSize,
      fontFamily: this.selectedFont,
      fill: this.selectedColor || '#000000',
      fontWeight: 'normal',
      fontStyle: 'normal',
      paintFirst: 'stroke',
      lockUniScaling: true,
      stroke: null,
      strokeWidth: 0
    };
    if (this.selectedStroke && this.selectedStrokeWidth > 0) {
      textOpts.stroke = this.selectedStroke;
      textOpts.strokeWidth = this.selectedStrokeWidth;
    }
    // Always deselect any existing object first
    this.canvas.discardActiveObject();
    this.activeTextObj = null;

    var text = new fabric.IText(val, textOpts);
    this._lockImageControls(text);
    this.canvas.add(text);
    this.canvas.setActiveObject(text);
    this.canvas.renderAll();
    this.activeTextObj = text;
    this._setAlpineData('textEditing', true);
    this.updateHasObjects();
    this.saveState();

    // Clear input for the next text entry
    inputs.forEach(function (inp) { inp.value = ''; });
    this.updatePreview('Your text');
  };

  // Update the text preview div (not canvas)
  InkbondStudio.prototype.updatePreview = function (text) {
    var self = this;
    if (!text) {
      var inputs = document.querySelectorAll('.js-studio-text-input');
      text = (inputs[0] ? inputs[0].value : '') || 'Your text';
    }
    var previews = document.querySelectorAll('.js-studio-text-preview-text');
    previews.forEach(function (el) {
      el.textContent = text;
      el.style.fontFamily = self.selectedFont;
      el.style.color = self.selectedColor;
      el.style.fontSize = Math.min(self.selectedSize, 32) + 'px';
      // Render outline via CSS text-stroke
      if (self.selectedStroke && self.selectedStrokeWidth > 0) {
        var sw = Math.min(self.selectedStrokeWidth, 5); // cap for preview
        el.style.webkitTextStroke = sw + 'px ' + self.selectedStroke;
        el.style.paintOrder = 'stroke fill';
      } else {
        el.style.webkitTextStroke = '';
        el.style.paintOrder = '';
      }
    });
  };

  // Live update: update canvas if editing, preview if creating
  InkbondStudio.prototype.updateLiveText = function (val) {
    if (this.activeTextObj && this.canvas.getObjects().indexOf(this.activeTextObj) !== -1) {
      // Editing existing text on canvas
      this.activeTextObj.set('text', val || 'Your text');
      this.canvas.renderAll();
    }
    // Always update preview
    this.updatePreview(val);
  };

  // ── Center Object ──
  InkbondStudio.prototype.centerObject = function (axis) {
    var obj = this.canvas.getActiveObject();
    if (!obj || obj._isGuide) return;
    if (axis === 'h') {
      obj.set('left', this.canvas.getWidth() / 2);
      obj.set('originX', 'center');
    } else if (axis === 'v') {
      obj.set('top', this.canvas.getHeight() / 2);
      obj.set('originY', 'center');
    }
    obj.setCoords();
    this.canvas.renderAll();
    this.saveState();
  };

  // ── Curve Text ──
  InkbondStudio.prototype.curveText = function (amount) {
    var obj = this._getTextTarget();
    if (!obj) return;

    if (amount === 0) {
      // Remove curve - set path to null
      obj.set('path', null);
      this.canvas.renderAll();
      this.saveState();
      return;
    }

    // Generate an SVG arc path for the curve
    var w = obj.width || 200;
    // Radius derived from curve amount: higher = tighter curve
    var radius = Math.max(50, (5000 / Math.abs(amount)));
    var sweep = amount > 0 ? 1 : 0;
    var pathStr = 'M 0 0 A ' + radius + ' ' + radius + ' 0 0 ' + sweep + ' ' + w + ' 0';

    try {
      var pathObj = new fabric.Path(pathStr, { visible: false });
      obj.set('path', pathObj);
      this.canvas.renderAll();
      this.saveState();
    } catch (e) {
      console.warn('Curve text error:', e);
    }
  };

  // ── Layers ──
  InkbondStudio.prototype.refreshLayers = function () {
    var self = this;
    var list = document.getElementById('ib-studio-layers-list');
    if (!list) return;

    var objects = this.canvas.getObjects().filter(function (obj) { return !obj._isGuide; });
    var active = this.canvas.getActiveObject();

    list.innerHTML = '';
    // Reverse order: top layer first
    for (var i = objects.length - 1; i >= 0; i--) {
      var obj = objects[i];
      var name = obj.type === 'i-text' || obj.type === 'textbox'
        ? '"' + (obj.text || '').substring(0, 16) + '"'
        : obj.type === 'image' ? 'Image' : 'Shape';

      var div = document.createElement('div');
      div.className = 'ib-studio__layer-item' + (obj === active ? ' active' : '');
      div.setAttribute('draggable', 'true');
      div.setAttribute('data-layer-idx', i);
      div.innerHTML = '<span class="ib-studio__layer-grip" title="Drag to reorder">⠿</span>' +
        '<span class="ib-studio__layer-item-name">' + name + '</span>' +
        '<span class="ib-studio__layer-arrows">' +
          '<button class="ib-studio__layer-arrow-btn js-layer-up" title="Move up">▲</button>' +
          '<button class="ib-studio__layer-arrow-btn js-layer-down" title="Move down">▼</button>' +
        '</span>';

      (function (fabricObj, idx) {
        div.addEventListener('click', function (e) {
          if (e.target.classList.contains('ib-studio__layer-grip')) return;
          if (e.target.classList.contains('ib-studio__layer-arrow-btn') || e.target.classList.contains('js-layer-up') || e.target.classList.contains('js-layer-down')) return;
          self.canvas.setActiveObject(fabricObj);
          self.canvas.renderAll();
          self.refreshLayers();
        });
        // Up/down arrow buttons
        div.querySelector('.js-layer-up').addEventListener('click', function (e) {
          e.stopPropagation();
          var allObjs = self.canvas.getObjects();
          var canvasIdx = allObjs.indexOf(fabricObj);
          if (canvasIdx < allObjs.length - 1) {
            self.canvas.moveObjectTo(fabricObj, canvasIdx + 1);
            self.canvas.renderAll();
            self.saveState();
            self.refreshLayers();
          }
        });
        div.querySelector('.js-layer-down').addEventListener('click', function (e) {
          e.stopPropagation();
          var allObjs = self.canvas.getObjects();
          var canvasIdx = allObjs.indexOf(fabricObj);
          var firstNonGuide = 0;
          for (var g = 0; g < allObjs.length; g++) { if (!allObjs[g]._isGuide) { firstNonGuide = g; break; } }
          if (canvasIdx > firstNonGuide) {
            self.canvas.moveObjectTo(fabricObj, canvasIdx - 1);
            self.canvas.renderAll();
            self.saveState();
            self.refreshLayers();
          }
        });
        // Drag & drop reordering
        div.addEventListener('dragstart', function (e) {
          e.dataTransfer.setData('text/plain', idx);
          div.style.opacity = '0.5';
        });
        div.addEventListener('dragend', function () { div.style.opacity = '1'; });
        div.addEventListener('dragover', function (e) { e.preventDefault(); div.style.background = '#e3f2fd'; });
        div.addEventListener('dragleave', function () { div.style.background = ''; });
        div.addEventListener('drop', function (e) {
          e.preventDefault();
          div.style.background = '';
          var fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
          var toIdx = idx;
          if (fromIdx === toIdx) return;
          // Reorder in canvas — convert reversed layer indices to canvas indices
          var nonGuides = self.canvas.getObjects().filter(function (o) { return !o._isGuide; });
          var fromObj = nonGuides[fromIdx];
          var toObj = nonGuides[toIdx];
          if (!fromObj || !toObj) return;
          var fromCanvasIdx = self.canvas.getObjects().indexOf(fromObj);
          var toCanvasIdx = self.canvas.getObjects().indexOf(toObj);
          self.canvas.moveObjectTo(fromObj, toCanvasIdx);
          self.canvas.renderAll();
          self.saveState();
          self.refreshLayers();
        });
      })(obj, i);

      list.appendChild(div);
    }
  };

  // Show pinch-to-zoom hint (once per session)
  InkbondStudio.prototype._showPinchHint = function () {
    if (this._pinchHintShown) return;
    // Only show on touch devices
    if (!('ontouchstart' in window)) return;
    this._pinchHintShown = true;
    var hint = document.createElement('div');
    hint.className = 'ib-studio__pinch-hint';
    hint.textContent = '\u{1F90F} Pinch to resize';
    var canvasArea = document.querySelector('.ib-studio__canvas-area');
    if (canvasArea) {
      canvasArea.appendChild(hint);
      setTimeout(function () { hint.classList.add('show'); }, 50);
      setTimeout(function () {
        hint.classList.remove('show');
        setTimeout(function () { if (hint.parentElement) hint.remove(); }, 500);
      }, 4000);
    }
  };

  // ── 1. Auto-save to localStorage ──
  var AUTOSAVE_KEY = 'inkbond_studio_autosave';

  InkbondStudio.prototype._setupAutoSave = function () {
    var self = this;
    // Save every 5 seconds if there are objects
    this._autoSaveInterval = setInterval(function () {
      var objs = self.canvas.getObjects().filter(function (o) { return !o._isGuide && !o._isSnapLine; });
      if (objs.length > 0) {
        try {
          var json = self.canvas.toJSON(['_isGuide', '_isSnapLine', 'lockUniScaling']);
          localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(json));
        } catch (e) { /* storage full or private mode */ }
      } else {
        localStorage.removeItem(AUTOSAVE_KEY);
      }
    }, 5000);
  };

  InkbondStudio.prototype._restoreAutoSave = function () {
    var self = this;
    try {
      var saved = localStorage.getItem(AUTOSAVE_KEY);
      if (!saved) return;
      var data = JSON.parse(saved);
      // Check if there are actual objects (not just guides)
      var hasContent = data.objects && data.objects.some(function (o) { return !o._isGuide && !o._isSnapLine; });
      if (!hasContent) return;

      // Show restore prompt
      var toast = document.createElement('div');
      toast.className = 'ib-studio__restore-toast';
      toast.innerHTML = '<span>Restore design?</span>' +
        '<button class="ib-studio__restore-yes">Restore</button>' +
        '<button class="ib-studio__restore-no">Start Fresh</button>';
      var canvasArea = document.querySelector('.ib-studio__canvas-area');
      if (canvasArea) canvasArea.appendChild(toast);
      setTimeout(function () { toast.classList.add('show'); }, 100);

      toast.querySelector('.ib-studio__restore-yes').addEventListener('click', function () {
        self.loadState(saved);
        toast.classList.remove('show');
        setTimeout(function () { toast.remove(); }, 300);
      });
      toast.querySelector('.ib-studio__restore-no').addEventListener('click', function () {
        localStorage.removeItem(AUTOSAVE_KEY);
        toast.classList.remove('show');
        setTimeout(function () { toast.remove(); }, 300);
      });

      // Auto-dismiss after 10s
      setTimeout(function () {
        if (toast.parentElement) {
          toast.classList.remove('show');
          setTimeout(function () { toast.remove(); }, 300);
        }
      }, 10000);
    } catch (e) { /* ignore parse errors */ }
  };

  // Clear autosave after successful add to cart
  InkbondStudio.prototype._clearAutoSave = function () {
    localStorage.removeItem(AUTOSAVE_KEY);
  };

  // ── 3. Double-tap to edit text ──
  InkbondStudio.prototype._setupDoubleTapEdit = function () {
    var self = this;
    this.canvas.on('mouse:dblclick', function (e) {
      var obj = e.target;
      if (!obj || obj._isGuide) return;
      if (obj.type === 'i-text' || obj.type === 'text') {
        // Adopt this text object for editing
        self.activeTextObj = obj;
        self.selectedColor = obj.fill || '#000000';
        self.selectedFont = obj.fontFamily || 'Arial';
        self.selectedSize = obj.fontSize || 24;
        self.selectedStroke = obj.stroke || null;
        self.selectedStrokeWidth = obj.strokeWidth || 0;

        // Open text panel
        self._setAlpineData('activePanel', 'text');
        self._setAlpineData('textEditing', true);

        // Sync all inputs
        var inputs = document.querySelectorAll('.js-studio-text-input');
        inputs.forEach(function (inp) { inp.value = obj.text || ''; });

        // Sync preview
        self.updatePreview(obj.text);

        // Sync color swatches
        self.syncPanelToSelection({ selected: [obj] });
      }
    });
  };

  // ── 4. Leave page warning ──
  InkbondStudio.prototype._setupLeaveWarning = function () {
    var self = this;
    this._addingToCart = false;
    window.addEventListener('beforeunload', function (e) {
      if (self._addingToCart) return; // Skip warning when adding to cart
      var objs = self.canvas.getObjects().filter(function (o) { return !o._isGuide && !o._isSnapLine; });
      if (objs.length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    });
  };

  // Sync mobile toolbar height to match canvas
  InkbondStudio.prototype._syncToolbarHeight = function () {
    var wrapper = document.getElementById('ib-studio-canvas-wrapper');
    if (wrapper) {
      var h = wrapper.offsetHeight;
      document.documentElement.style.setProperty('--ib-canvas-height', h + 'px');
    }
  };

  // Helper: lock image to corner-only scaling
  InkbondStudio.prototype._lockImageControls = function (img) {
    img.setControlsVisibility({
      mt: false, mb: false, ml: false, mr: false, // hide side handles
      tl: true, tr: true, bl: true, br: true,     // keep corners
      mtr: true                                     // keep rotation
    });
  };

  // ── Image upload (auto-placed on canvas) ──
  InkbondStudio.prototype.addImage = function (file) {
    var self = this;
    if (file.size > 10 * 1024 * 1024) {
      alert('File too large. Max size is 10MB.');
      return;
    }
    var reader = new FileReader();
    reader.onload = function (e) {
      var imgEl = new Image();
      imgEl.onload = function () {
        var fabricImg = new fabric.Image(imgEl);
        var maxW = self.canvas.getWidth() * 0.55;
        var maxH = self.canvas.getHeight() * 0.55;
        var scale = Math.min(maxW / fabricImg.width, maxH / fabricImg.height, 1);

        fabricImg.set({
          scaleX: scale, scaleY: scale,
          left: (self.canvas.getWidth() - fabricImg.width * scale) / 2,
          top: (self.canvas.getHeight() - fabricImg.height * scale) / 2,
          lockUniScaling: true
        });
        self._lockImageControls(fabricImg);

        self.canvas.add(fabricImg);
        self.canvas.setActiveObject(fabricImg);
        self.canvas.renderAll();
        self.saveState();
        self.updateHasObjects();
        self._showUploadHint();
      };
      imgEl.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Show one-time hint after photo upload
  InkbondStudio.prototype._showUploadHint = function () {
    if (this._uploadHintShown) return;
    this._uploadHintShown = true;
    var hint = document.getElementById('ib-studio-upload-hint');
    if (!hint) return;
    hint.style.display = 'flex';
    hint.style.opacity = '1';
    var timer = setTimeout(function () {
      hint.style.opacity = '0';
      setTimeout(function () { hint.style.display = 'none'; }, 300);
    }, 6000);
    var closeBtn = hint.querySelector('.js-upload-hint-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        clearTimeout(timer);
        hint.style.opacity = '0';
        setTimeout(function () { hint.style.display = 'none'; }, 300);
      });
    }
  };

  // Legacy addText kept for backward compat but now uses openTextPanel flow
  InkbondStudio.prototype.addText = function (customText) {
    var text = new fabric.IText(customText || 'Your text here', {
      left: this.canvas.getWidth() / 2,
      top: this.canvas.getHeight() / 2,
      originX: 'center',
      originY: 'center',
      fontSize: this.selectedSize,
      fontFamily: this.selectedFont,
      fill: this.selectedColor,
      fontWeight: 'normal',
      fontStyle: 'normal',
      paintFirst: 'stroke',
      lockUniScaling: true
    });
    this._lockImageControls(text);
    this.canvas.add(text);
    this.canvas.setActiveObject(text);
    this.canvas.renderAll();
    this.saveState();
    this.updateHasObjects();
  };

  InkbondStudio.prototype.duplicateSelected = function () {
    var self = this;
    var active = this.canvas.getActiveObject();
    if (!active || active._isGuide) return;

    function handleClone(cloned) {
      cloned.set({ left: active.left + 20, top: active.top + 20 });
      if (cloned.type === 'image' && cloned.lockUniScaling) self._lockImageControls(cloned);
      self.canvas.add(cloned);
      self.canvas.setActiveObject(cloned);
      self.canvas.renderAll();
      self.saveState();
      self.updateHasObjects();
      self.refreshLayers();
    }

    var result = active.clone();
    // Fabric.js v6 returns a Promise
    if (result && typeof result.then === 'function') {
      result.then(handleClone).catch(function (e) { console.warn('Clone failed:', e); });
    } else if (result) {
      handleClone(result);
    }
  };

  InkbondStudio.prototype.deleteSelected = function () {
    var active = this.canvas.getActiveObject();
    if (!active || active._isGuide) return;

    // If deleting the activeTextObj, clear reference
    if (active === this.activeTextObj) {
      this.activeTextObj = null;
      this._setAlpineData('textEditing', false);
    }

    if (active.type === 'activeSelection') {
      var self = this;
      var toRemove = [];
      active.forEachObject(function (obj) {
        if (!obj._isGuide) toRemove.push(obj);
      });
      this.canvas.discardActiveObject();
      toRemove.forEach(function (obj) { self.canvas.remove(obj); });
    } else {
      this.canvas.remove(active);
    }
    this.canvas.renderAll();
    this.saveState();
    this.updateHasObjects();
    this.refreshLayers();
  };

  // ── Layer ordering ──
  InkbondStudio.prototype.bringToFront = function () {
    var active = this.canvas.getActiveObject();
    if (active && !active._isGuide) { this.canvas.bringObjectToFront(active); this.canvas.renderAll(); this.saveState(); }
  };
  InkbondStudio.prototype.sendToBack = function () {
    var active = this.canvas.getActiveObject();
    if (active && !active._isGuide) { this.canvas.sendObjectToBack(active); this.canvas.renderAll(); this.saveState(); }
  };
  InkbondStudio.prototype.bringForward = function () {
    var active = this.canvas.getActiveObject();
    if (active && !active._isGuide) { this.canvas.bringObjectForward(active); this.canvas.renderAll(); this.saveState(); }
  };
  InkbondStudio.prototype.sendBackward = function () {
    var active = this.canvas.getActiveObject();
    if (active && !active._isGuide) { this.canvas.sendObjectBackwards(active); this.canvas.renderAll(); this.saveState(); }
  };

  InkbondStudio.prototype.clearCanvas = function () {
    if (!confirm('Clear your entire design? This cannot be undone.')) return;
    var self = this;
    var objects = this.canvas.getObjects().slice();
    objects.forEach(function (obj) { if (!obj._isGuide) self.canvas.remove(obj); });
    this.canvas.discardActiveObject();
    this.activeTextObj = null;
    this.canvas.renderAll();
    this.saveState();
    this.updateHasObjects();
    this.refreshLayers();
  };

  InkbondStudio.prototype.updateHasObjects = function () {
    var count = this.canvas.getObjects().filter(function (obj) { return !obj._isGuide; }).length;
    var el = document.getElementById('inkbond-studio');
    if (el && el._x_dataStack) {
      el._x_dataStack.forEach(function (data) {
        if (data.hasObjects !== undefined) data.hasObjects = count > 0;
      });
    }
  };

  // ── Text Property Updates ──
  // These update the active text object OR the selected canvas object
  InkbondStudio.prototype._getTextTarget = function () {
    // Prefer the active text object from the panel, then fall back to canvas selection
    if (this.activeTextObj && this.canvas.getObjects().indexOf(this.activeTextObj) !== -1) {
      return this.activeTextObj;
    }
    var obj = this.canvas.getActiveObject();
    if (obj && (obj.type === 'i-text' || obj.type === 'textbox')) return obj;
    return null;
  };

  InkbondStudio.prototype.updateTextColor = function (color) {
    this.selectedColor = color;
    var obj = this._getTextTarget();
    if (obj) {
      obj.set('fill', color);
      this.canvas.renderAll();
      this.saveState();
    }
    this.updatePreview();
  };

  InkbondStudio.prototype.updateTextFont = function (font) {
    this.selectedFont = font;
    var obj = this._getTextTarget();
    if (obj) {
      obj.set('fontFamily', font);
      this.canvas.renderAll();
      this.saveState();
    }
    this.updatePreview();
  };

  InkbondStudio.prototype.updateTextSize = function (size) {
    this.selectedSize = size;
    var obj = this._getTextTarget();
    if (obj) {
      obj.set('fontSize', size);
      this.canvas.renderAll();
      this.saveState();
    }
    this.updatePreview();
  };

  InkbondStudio.prototype.toggleBold = function () {
    var obj = this._getTextTarget();
    if (!obj) return false;
    var isBold = obj.fontWeight === 'bold';
    obj.set('fontWeight', isBold ? 'normal' : 'bold');
    this.canvas.renderAll();
    this.saveState();
    return !isBold;
  };

  InkbondStudio.prototype.toggleItalic = function () {
    var obj = this._getTextTarget();
    if (!obj) return false;
    var isItalic = obj.fontStyle === 'italic';
    obj.set('fontStyle', isItalic ? 'normal' : 'italic');
    this.canvas.renderAll();
    this.saveState();
    return !isItalic;
  };

  // ── Text Outline (stroke) ──
  InkbondStudio.prototype.updateTextStroke = function (color) {
    this.selectedStroke = color || null;
    var obj = this._getTextTarget();
    if (obj) {
      if (!color) {
        obj.set({ stroke: null, strokeWidth: 0 });
      } else {
        obj.set({ stroke: color, paintFirst: 'stroke' });
        if (!obj.strokeWidth || obj.strokeWidth === 0) {
          obj.set('strokeWidth', 1);
          this.selectedStrokeWidth = 1;
          var sliders = document.querySelectorAll('.js-studio-outline-slider');
          var values = document.querySelectorAll('.js-studio-outline-value');
          sliders.forEach(function (sl) { sl.value = 1; });
          values.forEach(function (sv) { sv.textContent = '1px'; });
        }
      }
      this.canvas.renderAll();
      this.saveState();
    }
    this.updatePreview();
  };

  InkbondStudio.prototype.updateTextStrokeWidth = function (width) {
    this.selectedStrokeWidth = width;
    var obj = this._getTextTarget();
    if (!obj) { this.updatePreview(); return; }
    obj.set('strokeWidth', width);
    this.canvas.renderAll();
    this.saveState();
    this.updatePreview();
  };

  // ── Image Processing (cutout.pro via Worker) ──
  InkbondStudio.prototype.processImage = function (type) {
    var self = this;
    var obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return Promise.reject(new Error('No image selected'));
    if (!WORKER_URL) return Promise.reject(new Error('Worker URL not configured'));

    var endpoint = type === 'face-cutout' ? '/face-cutout' : '/bg-remove';

    // Show processing state on Alpine
    this._setAlpineData('isProcessing', true);

    // Get the image element's source and downscale if needed (max 2048px)
    var imgEl = obj.getElement();
    var origW = imgEl.naturalWidth || imgEl.width;
    var origH = imgEl.naturalHeight || imgEl.height;
    var maxDim = 2048;
    var scale = Math.min(maxDim / origW, maxDim / origH, 1);
    var tempCanvas = document.createElement('canvas');
    tempCanvas.width = Math.round(origW * scale);
    tempCanvas.height = Math.round(origH * scale);
    var ctx = tempCanvas.getContext('2d');
    ctx.drawImage(imgEl, 0, 0, tempCanvas.width, tempCanvas.height);

    return new Promise(function (resolve, reject) {
      tempCanvas.toBlob(function (blob) {
        if (!blob) { self._setAlpineData('isProcessing', false); reject(new Error('Failed to read image')); return; }

        var formData = new FormData();
        formData.append('file', blob, 'image.png');

        // 25s timeout to avoid hanging forever
        var controller = new AbortController();
        var timeoutId = setTimeout(function () { controller.abort(); }, 25000);

        fetch(WORKER_URL + endpoint, { method: 'POST', body: formData, signal: controller.signal })
          .then(function (res) { clearTimeout(timeoutId); return res.json(); })
          .then(function (data) {
            if (!data.success || !data.image) {
              throw new Error(data.error || 'Processing failed');
            }
            var dataUrl = 'data:image/png;base64,' + data.image;
            return self._replaceActiveImage(obj, dataUrl);
          })
          .then(function () {
            self._setAlpineData('isProcessing', false);
            resolve();
          })
          .catch(function (err) {
            clearTimeout(timeoutId);
            self._setAlpineData('isProcessing', false);
            if (err.name === 'AbortError') {
              reject(new Error('Processing timed out. Try a smaller image.'));
            } else {
              reject(err);
            }
          });
      }, 'image/jpeg', 0.85); // Send as JPEG to reduce upload size
    });
  };

  InkbondStudio.prototype._replaceActiveImage = function (oldObj, dataUrl) {
    var self = this;
    return new Promise(function (resolve) {
      var imgEl = new Image();
      imgEl.onload = function () {
        var newImg = new fabric.Image(imgEl);
        // Preserve position, scale, rotation from original
        newImg.set({
          left: oldObj.left,
          top: oldObj.top,
          scaleX: oldObj.scaleX * (oldObj.width / newImg.width),
          scaleY: oldObj.scaleY * (oldObj.height / newImg.height),
          angle: oldObj.angle || 0,
          lockUniScaling: true,
          originX: oldObj.originX,
          originY: oldObj.originY
        });
        self._lockImageControls(newImg);
        self.canvas.remove(oldObj);
        self.canvas.add(newImg);
        self.canvas.setActiveObject(newImg);
        self.canvas.renderAll();
        self.saveState();
        resolve();
      };
      imgEl.src = dataUrl;
    });
  };

  // ── Art Library ──
  InkbondStudio.prototype.addArtToCanvas = function (imageUrl) {
    var self = this;
    var imgEl = new Image();
    imgEl.crossOrigin = 'anonymous';
    imgEl.onload = function () {
      var fabricImg = new fabric.Image(imgEl);
      var maxW = self.canvas.getWidth() * 0.55;
      var maxH = self.canvas.getHeight() * 0.55;
      var scale = Math.min(maxW / fabricImg.width, maxH / fabricImg.height, 1);

      fabricImg.set({
        scaleX: scale, scaleY: scale,
        left: (self.canvas.getWidth() - fabricImg.width * scale) / 2,
        top: (self.canvas.getHeight() - fabricImg.height * scale) / 2,
        lockUniScaling: true
      });
      self._lockImageControls(fabricImg);

      self.canvas.add(fabricImg);
      self.canvas.setActiveObject(fabricImg);
      self.canvas.renderAll();
      self.saveState();
      self.updateHasObjects();
    };
    imgEl.src = imageUrl;
  };

  // ── Alpine.js data helper ──
  InkbondStudio.prototype._setAlpineData = function (key, value) {
    var el = document.getElementById('inkbond-studio');
    if (el && el._x_dataStack) {
      el._x_dataStack.forEach(function (data) {
        if (data[key] !== undefined) data[key] = value;
      });
    }
  };

  // ── Image Action Toolbar ──
  InkbondStudio.prototype._showImageActions = function (obj) {
    var toolbar = document.getElementById('ib-studio-image-actions');
    if (!toolbar || !obj || obj.type !== 'image') {
      this._hideImageActions();
      return;
    }
    // Position toolbar above the selected image
    var canvasEl = this.canvas.getElement();
    var canvasRect = canvasEl.getBoundingClientRect();
    var bound = obj.getBoundingRect();

    toolbar.style.display = 'flex';
    toolbar.style.left = (bound.left + bound.width / 2) + 'px';
    toolbar.style.top = Math.max(bound.top - 8, 4) + 'px';
  };

  InkbondStudio.prototype._hideImageActions = function () {
    var toolbar = document.getElementById('ib-studio-image-actions');
    if (toolbar) toolbar.style.display = 'none';
  };

  // ── Edit pencil icon (yellow, appears on selection) ──
  InkbondStudio.prototype._showEditPencil = function (obj) {
    var pencil = document.getElementById('ib-studio-edit-pencil');
    if (!pencil || !obj || obj._isGuide) { this._hideEditPencil(); return; }
    pencil.style.display = 'flex';
    var self = this;
    pencil.onclick = function () {
      if (obj.type === 'i-text' || obj.type === 'text') {
        self._setAlpineData('activePanel', 'text');
        self.syncPanelToSelection({ selected: [obj] });
      } else if (obj.type === 'image') {
        self._setAlpineData('activePanel', 'upload');
      }
    };
  };
  InkbondStudio.prototype._hideEditPencil = function () {
    var pencil = document.getElementById('ib-studio-edit-pencil');
    if (pencil) pencil.style.display = 'none';
  };

  // ── Export ──
  InkbondStudio.prototype.prepareExport = function () {
    var self = this;
    return new Promise(function (resolve) {
      self.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) obj.set('visible', false);
      });
      self.canvas.discardActiveObject();

      // Temporarily set white background for preview
      var origBg = self.canvas.backgroundColor;
      self.canvas.backgroundColor = '#ffffff';
      self.canvas.renderAll();

      var preview = self.canvas.toDataURL({ format: 'jpeg', quality: 0.85, multiplier: 1 });

      // Restore transparent background
      self.canvas.backgroundColor = origBg;
      self.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) obj.set('visible', true);
      });
      self.canvas.renderAll();

      self.previewDataUrl = preview;
      resolve(preview);
    });
  };

  // Helper: convert data URL to Blob for upload
  InkbondStudio.prototype._dataUrlToBlob = function (dataUrl) {
    var parts = dataUrl.split(',');
    var mime = parts[0].match(/:(.*?);/)[1];
    var byteString = atob(parts[1]);
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);
    for (var i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    return new Blob([ab], { type: mime });
  };

  // Upload image to Cloudflare R2 via Worker
  InkbondStudio.prototype._uploadImage = function (dataUrl, type) {
    var blob = this._dataUrlToBlob(dataUrl);
    var form = new FormData();
    form.append('file', blob, type === 'preview' ? 'preview.jpg' : 'production.png');
    form.append('type', type);

    return fetch(WORKER_URL + '/upload', { method: 'POST', body: form })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (!data.success) throw new Error(data.error || 'Upload failed');
        return data.url;
      });
  };

  InkbondStudio.prototype.addToCart = function (variantId) {
    var self = this;
    var vid = variantId || VARIANT_ID;
    return new Promise(function (resolve, reject) {
      if (!self.previewDataUrl) { reject(new Error('No preview generated')); return; }
      if (!vid) { reject(new Error('No variant selected')); return; }

      // Generate high-res transparent PNG production export (300 DPI)
      self.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) obj.set('visible', false);
      });
      self.canvas.discardActiveObject();

      // Temporarily remove background for transparent export
      var origBg = self.canvas.backgroundColor;
      self.canvas.backgroundColor = null;
      self.canvas.renderAll();

      var productionDataUrl = self.canvas.toDataURL({
        format: 'png', quality: 1,
        multiplier: PROD_WIDTH / self.canvas.getWidth()
      });

      // Restore background and guides
      self.canvas.backgroundColor = origBg;
      self.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) obj.set('visible', true);
      });
      self.canvas.renderAll();

      // Upload both preview and production images to R2
      Promise.all([
        self._uploadImage(self.previewDataUrl, 'preview'),
        self._uploadImage(productionDataUrl, 'production')
      ])
      .then(function (urls) {
        var previewUrl = urls[0];
        var prodUrl = urls[1];

        var props = {
          '_customily-preview': previewUrl,
          '_customily-production-url': prodUrl,
          '_customily-shop-preview': previewUrl,
          '_customily-thumb': previewUrl,
          '_studio-design': 'true'
        };

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: vid, quantity: 1, properties: props }] })
        });
      })
      .then(function (res) { if (!res.ok) throw new Error('Cart add failed'); return res.json(); })
      .then(function () { self._clearAutoSave(); resolve(); })
      .catch(function (err) { reject(err); });
    });
  };

  // ── Event Bindings ──
  InkbondStudio.prototype.bindEvents = function () {
    var self = this;

    // File input
    var fileInput = document.getElementById('ib-studio-file-input');
    if (fileInput) {
      fileInput.addEventListener('change', function (e) {
        var files = e.target.files;
        for (var i = 0; i < files.length; i++) {
          if (files[i].type.indexOf('image') === 0) self.addImage(files[i]);
        }
        fileInput.value = '';
      });
    }

    // Drag & drop on upload zone
    var dropZone = document.getElementById('ib-studio-drop-zone');
    if (dropZone) {
      dropZone.addEventListener('dragover', function (e) {
        e.preventDefault();
        dropZone.style.borderColor = '#c4a882';
        dropZone.style.background = '#faf6f0';
      });
      dropZone.addEventListener('dragleave', function () {
        dropZone.style.borderColor = '#d0d0d0';
        dropZone.style.background = '';
      });
      dropZone.addEventListener('drop', function (e) {
        e.preventDefault();
        dropZone.style.borderColor = '#d0d0d0';
        dropZone.style.background = '';
        var files = e.dataTransfer.files;
        for (var i = 0; i < files.length; i++) {
          if (files[i].type.indexOf('image') === 0) self.addImage(files[i]);
        }
      });
    }

    // Also allow drag & drop on canvas
    var canvasArea = document.querySelector('.ib-studio__canvas-area');
    if (canvasArea) {
      canvasArea.addEventListener('dragover', function (e) { e.preventDefault(); });
      canvasArea.addEventListener('drop', function (e) {
        e.preventDefault();
        var files = e.dataTransfer.files;
        for (var i = 0; i < files.length; i++) {
          if (files[i].type.indexOf('image') === 0) self.addImage(files[i]);
        }
      });
    }

    // ── Live text input: update active text object as user types ──
    var textInputs = document.querySelectorAll('.js-studio-text-input');
    textInputs.forEach(function (input) {
      input.addEventListener('input', function () {
        // Sync all text inputs
        var val = this.value;
        textInputs.forEach(function (inp) {
          if (inp !== input) inp.value = val;
        });
        self.updateLiveText(val);
      });
    });

    // Object modified -> save state
    this.canvas.on('object:modified', function () { self.saveState(); });

    // Selection events -> sync panels + show image actions + layers
    this.canvas.on('selection:created', function (e) {
      self._setAlpineData('hasSelection', true);
      self.syncPanelToSelection(e);
      self.refreshLayers();
      var obj = e.selected ? e.selected[0] : null;
      if (obj && obj.type === 'image') {
        self._showImageActions(obj);
        self._showPinchHint();
      }
      else self._hideImageActions();
      if (obj && !obj._isGuide) self._showEditPencil(obj);
    });
    this.canvas.on('selection:updated', function (e) {
      self._setAlpineData('hasSelection', true);
      self.syncPanelToSelection(e);
      self.refreshLayers();
      var obj = e.selected ? e.selected[0] : null;
      if (obj && obj.type === 'image') self._showImageActions(obj);
      else self._hideImageActions();
      if (obj && !obj._isGuide) self._showEditPencil(obj);
    });
    this.canvas.on('selection:cleared', function () {
      self._setAlpineData('hasSelection', false);
      self._hideImageActions();
      self._hideEditPencil();
      self._setAlpineData('textEditing', false);
      self.activeTextObj = null;
      self.refreshLayers();
    });
    this.canvas.on('object:moving', function () { self._hideImageActions(); self._hideEditPencil(); });
    this.canvas.on('object:scaling', function (e) {
      self._hideImageActions();
      var obj = e.target;
      if (!obj) return;
      // Cap image scaling at 400% of original pixel size (scaleX/Y relative to raw image)
      if (obj.type === 'image') {
        var maxScale = 4.0;
        if (obj.scaleX > maxScale) { obj.scaleX = maxScale; obj.scaleY = maxScale; }
        if (obj.scaleY > maxScale) { obj.scaleY = maxScale; obj.scaleX = maxScale; }
      }
    });

    // ── Color swatches (both desktop + mobile via querySelectorAll) ──
    // Exclude outline swatches so clicking outline doesn't also set fill color
    var allSwatches = document.querySelectorAll('.ib-studio__swatch:not(.js-studio-outline-swatch)');
    allSwatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        allSwatches.forEach(function (s) { s.classList.remove('active'); });
        allSwatches.forEach(function (s) {
          if (s.getAttribute('data-color') === swatch.getAttribute('data-color')) s.classList.add('active');
        });
        self.updateTextColor(swatch.getAttribute('data-color'));
      });
    });

    // ── Font picker (both desktop + mobile) ──
    var allFontBtns = document.querySelectorAll('.ib-studio__font-card');
    allFontBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        allFontBtns.forEach(function (b) { b.classList.remove('active'); });
        allFontBtns.forEach(function (b) {
          if (b.getAttribute('data-font') === btn.getAttribute('data-font')) b.classList.add('active');
        });
        self.updateTextFont(btn.getAttribute('data-font'));
      });
    });

    // ── Size sliders (all of them) ──
    var sizeSliders = document.querySelectorAll('.js-studio-size-slider');
    var sizeValues = document.querySelectorAll('.js-studio-size-value');
    sizeSliders.forEach(function (slider) {
      slider.addEventListener('input', function () {
        var val = parseInt(this.value, 10);
        // Sync all size displays
        sizeValues.forEach(function (sv) { sv.textContent = val + 'px'; });
        sizeSliders.forEach(function (sl) { sl.value = val; });
        self.updateTextSize(val);
      });
    });

    // ── Bold / Italic buttons (all of them) ──
    var boldBtns = document.querySelectorAll('.js-studio-bold');
    boldBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isBold = self.toggleBold();
        boldBtns.forEach(function (b) { b.classList.toggle('active', isBold); });
      });
    });

    var italicBtns = document.querySelectorAll('.js-studio-italic');
    italicBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isItalic = self.toggleItalic();
        italicBtns.forEach(function (b) { b.classList.toggle('active', isItalic); });
      });
    });

    // ── Outline color swatches (both desktop + mobile) ──
    var outlineSwatches = document.querySelectorAll('.js-studio-outline-swatch');
    outlineSwatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        outlineSwatches.forEach(function (s) { s.classList.remove('active'); });
        var color = swatch.getAttribute('data-outline-color');
        outlineSwatches.forEach(function (s) {
          if (s.getAttribute('data-outline-color') === color) s.classList.add('active');
        });
        self.updateTextStroke(color || null);
      });
    });

    // ── Outline color picker ──
    var outlineColorPickers = document.querySelectorAll('.js-studio-outline-color-picker');
    outlineColorPickers.forEach(function (picker) {
      picker.addEventListener('input', function (e) {
        e.stopPropagation();
        var color = this.value;
        outlineSwatches.forEach(function (s) { s.classList.remove('active'); });
        this.closest('.ib-studio__swatch--picker').style.background = color;
        self.selectedStroke = color;
        self.updateTextStroke(color);
        var previews = document.querySelectorAll('.js-studio-text-preview-text');
        previews.forEach(function (el) {
          el.style.webkitTextStrokeColor = color;
        });
      });
      picker.addEventListener('change', function (e) {
        e.stopPropagation();
        var color = this.value;
        self.selectedStroke = color;
        self.updateTextStroke(color);
      });
    });

    // ── Outline thickness sliders ──
    var outlineSliders = document.querySelectorAll('.js-studio-outline-slider');
    var outlineValues = document.querySelectorAll('.js-studio-outline-value');
    outlineSliders.forEach(function (slider) {
      slider.addEventListener('input', function () {
        var val = parseInt(this.value, 10);
        outlineValues.forEach(function (sv) { sv.textContent = val + 'px'; });
        outlineSliders.forEach(function (sl) { sl.value = val; });
        self.updateTextStrokeWidth(val);
      });
    });

    // ── Image scale sliders ──
    var imgScaleSliders = document.querySelectorAll('.js-studio-img-scale');
    var imgScaleValues = document.querySelectorAll('.js-studio-img-scale-value');
    imgScaleSliders.forEach(function (slider) {
      slider.addEventListener('input', function () {
        var pct = parseInt(this.value, 10);
        imgScaleValues.forEach(function (sv) { sv.textContent = pct + '%'; });
        imgScaleSliders.forEach(function (sl) { sl.value = pct; });
        var obj = self.canvas.getActiveObject();
        if (obj && obj.type === 'image') {
          var baseScale = obj._baseScale || obj.scaleX;
          if (!obj._baseScale) obj._baseScale = obj.scaleX;
          var newScale = baseScale * (pct / 100);
          obj.set({ scaleX: newScale, scaleY: newScale });
          obj.setCoords();
          self.canvas.renderAll();
        }
      });
      slider.addEventListener('change', function () { self.saveState(); });
    });

    // ── Custom color picker (both desktop + mobile) ──
    var colorPickers = document.querySelectorAll('.js-studio-color-picker');
    colorPickers.forEach(function (picker) {
      picker.addEventListener('input', function (e) {
        e.stopPropagation();
        var color = this.value;
        allSwatches.forEach(function (s) { s.classList.remove('active'); });
        this.closest('.ib-studio__swatch--picker').style.background = color;
        self.selectedColor = color;
        self.updateTextColor(color);
      });
      picker.addEventListener('change', function (e) {
        e.stopPropagation();
        // 'change' fires when picker is closed — final commit
        var color = this.value;
        self.selectedColor = color;
        self.updateTextColor(color);
      });
    });

    // ── Curve sliders ──
    var curveSliders = document.querySelectorAll('.js-studio-curve-slider');
    var curveValues = document.querySelectorAll('.js-studio-curve-value');
    curveSliders.forEach(function (slider) {
      slider.addEventListener('input', function () {
        var val = parseInt(this.value, 10);
        curveValues.forEach(function (sv) { sv.textContent = val; });
        curveSliders.forEach(function (sl) { sl.value = val; });
        self.curveText(val);
      });
    });
  };

  InkbondStudio.prototype.syncPanelToSelection = function (e) {
    var obj = e.selected ? e.selected[0] : null;
    if (!obj || (obj.type !== 'i-text' && obj.type !== 'textbox')) return;

    // Adopt the selected text object as the active one for panel controls
    this.activeTextObj = obj;
    this._setAlpineData('textEditing', true);

    // Sync color swatches
    var swatches = document.querySelectorAll('.ib-studio__swatch');
    swatches.forEach(function (s) {
      s.classList.toggle('active', s.getAttribute('data-color') === obj.fill);
    });

    // Sync font cards
    var fontBtns = document.querySelectorAll('.ib-studio__font-card');
    fontBtns.forEach(function (b) {
      b.classList.toggle('active', b.getAttribute('data-font') === obj.fontFamily);
    });

    // Sync size sliders
    var sizeSliders = document.querySelectorAll('.js-studio-size-slider');
    var sizeValues = document.querySelectorAll('.js-studio-size-value');
    sizeSliders.forEach(function (sl) { sl.value = obj.fontSize; });
    sizeValues.forEach(function (sv) { sv.textContent = obj.fontSize + 'px'; });

    // Sync bold/italic
    var boldBtns = document.querySelectorAll('.js-studio-bold');
    var italicBtns = document.querySelectorAll('.js-studio-italic');
    boldBtns.forEach(function (b) { b.classList.toggle('active', obj.fontWeight === 'bold'); });
    italicBtns.forEach(function (b) { b.classList.toggle('active', obj.fontStyle === 'italic'); });

    // Sync text inputs
    var textInputs = document.querySelectorAll('.js-studio-text-input');
    textInputs.forEach(function (inp) { inp.value = obj.text || ''; });

    // Sync outline swatches
    var outlineSwatches = document.querySelectorAll('.js-studio-outline-swatch');
    var currentStroke = obj.stroke || '';
    outlineSwatches.forEach(function (s) {
      s.classList.toggle('active', s.getAttribute('data-outline-color') === currentStroke);
    });

    // Sync outline sliders
    var outlineSliders = document.querySelectorAll('.js-studio-outline-slider');
    var outlineValues = document.querySelectorAll('.js-studio-outline-value');
    var sw = obj.strokeWidth || 0;
    outlineSliders.forEach(function (sl) { sl.value = sw; });
    outlineValues.forEach(function (sv) { sv.textContent = sw + 'px'; });
  };

  // ── Share Design ──
  InkbondStudio.prototype.shareDesign = function () {
    var self = this;
    // Generate preview image
    this.canvas.getObjects().forEach(function (obj) {
      if (obj._isGuide) obj.set('visible', false);
    });
    this.canvas.discardActiveObject();
    this.canvas.renderAll();

    var dataUrl = this.canvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });

    this.canvas.getObjects().forEach(function (obj) {
      if (obj._isGuide) obj.set('visible', true);
    });
    this.canvas.renderAll();

    // Try native share API (mobile), fall back to download
    if (navigator.share && navigator.canShare) {
      fetch(dataUrl)
        .then(function (res) { return res.blob(); })
        .then(function (blob) {
          var file = new File([blob], 'inkbond-design.png', { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            return navigator.share({
              title: 'My Inkbond Tattoo Design',
              text: 'Check out my custom tattoo design!',
              files: [file]
            });
          } else {
            self._downloadDesign(dataUrl);
          }
        })
        .catch(function () { self._downloadDesign(dataUrl); });
    } else {
      this._downloadDesign(dataUrl);
    }
  };

  InkbondStudio.prototype._downloadDesign = function (dataUrl) {
    var link = document.createElement('a');
    link.download = 'inkbond-design.png';
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Pinch-to-Zoom (mobile) ──
  InkbondStudio.prototype._setupPinchZoom = function () {
    var self = this;
    var lastDist = 0;
    var initialScaleX = 1;
    var initialScaleY = 1;
    var isPinching = false;
    var pinchTarget = null;

    function getDistance(touches) {
      var dx = touches[0].clientX - touches[1].clientX;
      var dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    var wrapper = document.getElementById('ib-studio-canvas-wrapper');
    if (!wrapper) return;

    wrapper.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2) {
        var active = self.canvas.getActiveObject();
        if (active && !active._isGuide) {
          isPinching = true;
          pinchTarget = active;
          lastDist = getDistance(e.touches);
          initialScaleX = active.scaleX;
          initialScaleY = active.scaleY;
          e.preventDefault();
        }
      }
    }, { passive: false });

    wrapper.addEventListener('touchmove', function (e) {
      if (!isPinching || !pinchTarget || e.touches.length !== 2) return;
      e.preventDefault();

      var dist = getDistance(e.touches);
      var rawRatio = dist / lastDist;
      // Reduce sensitivity by 50% — dampen the ratio toward 1.0
      var ratio = 1 + (rawRatio - 1) * 0.5;
      var newScaleX = initialScaleX * ratio;
      var newScaleY = initialScaleY * ratio;

      // Cap image scaling at 400%
      if (pinchTarget.type === 'image') {
        newScaleX = Math.min(newScaleX, 4.0);
        newScaleY = Math.min(newScaleY, 4.0);
      }
      // Min scale 10%
      newScaleX = Math.max(newScaleX, 0.1);
      newScaleY = Math.max(newScaleY, 0.1);

      if (pinchTarget.lockUniScaling) {
        pinchTarget.scaleX = newScaleX;
        pinchTarget.scaleY = newScaleX;
      } else {
        pinchTarget.scaleX = newScaleX;
        pinchTarget.scaleY = newScaleY;
      }
      pinchTarget.setCoords();
      self.canvas.renderAll();
    }, { passive: false });

    wrapper.addEventListener('touchend', function (e) {
      if (isPinching && e.touches.length < 2) {
        isPinching = false;
        if (pinchTarget) {
          pinchTarget.setCoords();
          self.saveState();
        }
        pinchTarget = null;
      }
    });

  };

  // ── Initialize ──
  waitForFabric(function () {
    window.inkbondStudio = new InkbondStudio();
  });

})();
